'use client';
import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const LAST_UPDATED = 'March 26, 2026';
const COMPANY = 'DA1MAMBA Professional Detailing';
const EMAIL = 'info@da1mambadetailing.com';
const PHONE_DISPLAY = '(949) 293-5721';
const CALL_LINK = 'tel:+19492935721';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      'When you contact us, book a service, or submit a form on our website, we may collect personal information such as your name, phone number, email address, vehicle information, and service location.',
      'We may also automatically collect certain technical information when you visit our site, including your IP address, browser type, pages visited, and time spent on pages. This information is used solely to improve our website experience.',
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      'We use the information we collect to schedule and confirm detailing appointments, respond to inquiries, send service reminders and follow-ups, and improve our services.',
      'We do not sell, rent, or trade your personal information to third parties. Your information is used exclusively to provide and improve our detailing services.',
    ],
  },
  {
    title: 'SMS and Text Communications',
    content: [
      'By texting us or providing your phone number, you consent to receive SMS messages related to your service inquiry, appointment confirmation, and follow-up communications from DA1MAMBA Professional Detailing.',
      'Message and data rates may apply. You may opt out at any time by replying STOP to any text message we send.',
    ],
  },
  {
    title: 'Data Security',
    content: [
      'We take reasonable precautions to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the internet or electronic storage is 100% secure.',
      'We encourage you to use caution when sharing personal information online.',
    ],
  },
  {
    title: 'Third-Party Services',
    content: [
      'We may use third-party services such as Square for appointment booking and payment processing. These services have their own privacy policies, and we encourage you to review them.',
      'Our website may contain links to external sites. We are not responsible for the privacy practices of those sites.',
    ],
  },
  {
    title: 'Cookies',
    content: [
      'Our website may use cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, though some features of the site may not function properly as a result.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'You have the right to request access to, correction of, or deletion of your personal information that we hold. To make such a request, please contact us using the information below.',
      'California residents may have additional rights under the California Consumer Privacy Act (CCPA). Please contact us for more information.',
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of our website after changes constitutes acceptance of the updated policy.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      `If you have any questions about this Privacy Policy or how we handle your information, please contact us at ${EMAIL} or call us at ${PHONE_DISPLAY}.`,
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div style={{ background: 'var(--mamba-black)', minHeight: '100vh' }}>
      <Header />
      <main>
        {/* Hero */}
        <section
          style={{
            paddingTop: '140px',
            paddingBottom: '80px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 60% at 20% 40%, rgba(107,33,168,0.1) 0%, transparent 70%)' }}
          />
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <div className="label-gold mb-5">
              <span style={{ width: '40px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Legal
            </div>
            <h1
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(36px, 5vw, 64px)',
                lineHeight: '1.0',
                letterSpacing: '-0.03em',
                color: 'var(--white)',
                marginBottom: '20px',
              }}
            >
              Privacy{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, var(--mamba-purple-bright), #9333ea 50%, var(--mamba-gold-light))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Policy
              </span>
            </h1>
            <p style={{ color: 'var(--gray-dim)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.04em' }}>
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ paddingBottom: '120px' }}>
          <div className="max-w-4xl mx-auto px-6">
            {/* Intro */}
            <div
              style={{
                background: 'var(--mamba-graphite)',
                border: '1px solid rgba(107,33,168,0.2)',
                borderRadius: '2px',
                padding: '32px',
                marginBottom: '48px',
              }}
            >
              <p style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                {COMPANY} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our services.
              </p>
            </div>

            {/* Sections */}
            <div className="flex flex-col gap-10">
              {sections?.map((section, i) => (
                <div
                  key={i}
                  style={{
                    borderBottom: '1px solid rgba(107,33,168,0.12)',
                    paddingBottom: '40px',
                  }}
                >
                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      fontSize: '20px',
                      color: 'var(--white)',
                      marginBottom: '16px',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {i + 1}. {section?.title}
                  </h2>
                  <div className="flex flex-col gap-4">
                    {section?.content?.map((para, j) => (
                      <p
                        key={j}
                        style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif', margin: 0 }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div
              style={{
                marginTop: '60px',
                background: 'var(--mamba-graphite)',
                border: '1px solid rgba(107,33,168,0.25)',
                borderRadius: '2px',
                padding: '40px',
                textAlign: 'center',
              }}
            >
              <div className="label-gold mb-4" style={{ justifyContent: 'center' }}>Questions?</div>
              <p style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '24px' }}>
                We're happy to answer any questions about how we handle your data.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`mailto:${EMAIL}`}
                  className="btn-primary"
                  style={{ fontSize: '13px', padding: '14px 28px' }}
                >
                  Email Us
                </a>
                <a
                  href={CALL_LINK}
                  className="btn-gold"
                  style={{ fontSize: '13px', padding: '13px 24px' }}
                >
                  Call {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
